<div class="min-h-screen bg-white">
  <!-- Mobile Overlay -->
  @if (mobileMenuOpen()) {
    <div
      class="fixed inset-0 bg-gray-900/60 z-40 lg:hidden transition-opacity"
      (click)="closeMobileMenu()"
    ></div>
  }

  <!-- Sidebar -->
  <aside
    [class]="
      'fixed lg:fixed top-0 left-0 h-full z-50 bg-white border-r border-gray-200 transition-all duration-300 ease-out ' +
      (sidebarCollapsed() ? 'w-20' : 'w-64') +
      (mobileMenuOpen() ? ' translate-x-0' : ' -translate-x-full lg:translate-x-0')
    "
  >
    <div class="relative h-full flex flex-col">
      <!-- Logo Section -->
      <div class="px-3 py-2 border-b border-gray-200">
        <div class="flex items-center justify-between">
          @if (!sidebarCollapsed()) {
            <div class="flex items-center gap-2.5">
              <div class="w-8 h-8 bg-accent rounded-lg flex items-center justify-center">
                <i class="fas fa-shield-alt text-black text-sm"></i>
              </div>
              <div>
                <h1 class="text-sm font-bold text-gray-900 tracking-tight leading-tight">
                  {{ translationService.t('adminNav.panel') }}
                </h1>
                <p class="text-xs text-gray-500 font-medium">Morichal Corp</p>
              </div>
            </div>
          } @else {
            <div class="w-8 h-8 bg-accent rounded-lg flex items-center justify-center mx-auto">
              <i class="fas fa-shield-alt text-black text-sm"></i>
            </div>
          }
          <button
            [attr.aria-label]="translationService.t('a11y.toggleSidebar')"
            (click)="toggleSidebar()"
            class="hidden lg:flex w-7 h-7 items-center justify-center text-gray-500 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors"
            [class.ml-auto]="sidebarCollapsed()"
          >
            <i
              [class]="
                sidebarCollapsed() ? 'fas fa-chevron-right text-xs' : 'fas fa-chevron-left text-xs'
              "
            ></i>
          </button>
          <!-- Mobile Close -->
          <button
            [attr.aria-label]="translationService.t('common.close')"
            (click)="closeMobileMenu()"
            class="lg:hidden w-7 h-7 flex items-center justify-center text-gray-500 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 py-2 px-2 overflow-y-auto">
        @if (!sidebarCollapsed()) {
          <p class="text-xs font-bold text-gray-500 uppercase tracking-wider px-2 mb-1">
            {{ translationService.t('adminNav.mainMenu') }}
          </p>
        }
        <div class="space-y-0.5">
          @for (item of visibleNavItems; track item.route) {
            <a
              [routerLink]="item.route"
              routerLinkActive="bg-accent-50 !text-accent-700 border-l-2 border-accent [&>div]:bg-accent-100 [&>div]:!text-accent-700"
              [routerLinkActiveOptions]="{ exact: false }"
              [title]="translationService.t(item.labelKey)"
              [attr.aria-label]="translationService.t(item.labelKey)"
              class="group flex items-center gap-2.5 px-2 py-1.5 rounded-lg border-l-2 border-transparent text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
              [class.justify-center]="sidebarCollapsed()"
              (click)="closeMobileMenu()"
            >
              <div
                class="w-7 h-7 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 group-hover:text-gray-700 transition-colors"
              >
                <i [class]="item.icon + ' text-xs'"></i>
              </div>
              @if (!sidebarCollapsed()) {
                <span class="font-medium text-sm">{{ translationService.t(item.labelKey) }}</span>
              }
            </a>
          }
        </div>
      </nav>

      <!-- Bottom Section -->
      <div class="p-2 border-t border-gray-200">
        @if (!sidebarCollapsed()) {
          <button
            (click)="backToApp()"
            class="w-full py-1.5 px-3 bg-white text-gray-900 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
          >
            <i class="fas fa-arrow-left text-xs"></i>
            {{ translationService.t('adminNav.backToApp') }}
          </button>
        } @else {
          <button
            (click)="backToApp()"
            [title]="translationService.t('adminNav.backToApp')"
            class="w-full py-2.5 bg-white text-gray-900 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-center"
          >
            <i class="fas fa-arrow-left"></i>
          </button>
        }
      </div>
    </div>
  </aside>

  <!-- Main Content -->
  <div
    class="transition-all duration-300 ease-out"
    [class.lg:ml-64]="!sidebarCollapsed()"
    [class.lg:ml-20]="sidebarCollapsed()"
  >
    <!-- Top Header -->
    <header class="sticky top-0 z-30 bg-white border-b border-gray-200">
      <div class="h-12 px-4 lg:px-5 flex items-center justify-between">
        <!-- Left Side -->
        <div class="flex items-center gap-3">
          <!-- Mobile Menu Button -->
          <button
            [attr.aria-label]="translationService.t('a11y.openMenu')"
            (click)="toggleMobileMenu()"
            class="lg:hidden w-8 h-8 flex items-center justify-center text-gray-500 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors"
          >
            <i class="fas fa-bars text-base"></i>
          </button>

          <!-- Breadcrumb / Title -->
          <div class="hidden sm:block">
            <div class="flex items-center gap-2 text-sm">
              <span class="text-gray-400">
                <i class="fas fa-home text-xs"></i>
              </span>
              <span class="text-gray-500">/</span>
              <span class="font-semibold text-gray-900">{{
                translationService.t('adminNav.administration')
              }}</span>
            </div>
          </div>
        </div>

        <!-- Right Side -->
        <div class="flex items-center gap-1.5">
          <!-- Notifications -->
          <button
            (click)="openNotifications()"
            [attr.aria-label]="translationService.t('a11y.notifications')"
            class="relative w-8 h-8 flex items-center justify-center text-gray-500 hover:text-accent-700 hover:bg-accent-50 rounded-lg transition-colors"
          >
            <i class="fas fa-bell text-sm"></i>
            @if (notificationCenter.hasUnread()) {
              <span class="absolute top-1.5 right-1.5 w-2 h-2 bg-accent rounded-full"></span>
            }
          </button>

          <app-language-switcher variant="dropdown" tone="light"></app-language-switcher>

          <!-- Divider -->
          <div class="hidden sm:block w-px h-7 bg-gray-200 mx-1.5"></div>

          <!-- User Menu -->
          <div class="relative">
            <button
              (click)="toggleUserMenu()"
              class="flex items-center gap-2.5 py-1 pl-1 pr-2.5 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <div class="relative">
                <img
                  src="https://storage.googleapis.com/uxpilot-auth.appspot.com/avatars/avatar-1.jpg"
                  [alt]="translationService.t('nav.userAvatar')"
                  class="w-7 h-7 rounded-lg object-cover ring-2 ring-accent-100"
                />
                <span
                  class="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-accent rounded-full border-2 border-white"
                ></span>
              </div>
              <div class="hidden sm:block text-left leading-tight">
                <p class="text-sm font-semibold text-gray-900 leading-tight">
                  {{
                    currentUser?.full_name ||
                      currentUser?.first_name ||
                      currentUser?.username ||
                      translationService.t('nav.administrator')
                  }}
                </p>
                <p class="text-xs text-gray-500 leading-tight">
                  {{ roleText() }}
                </p>
              </div>
              <i class="fas fa-chevron-down text-xs text-gray-400 hidden sm:block"></i>
            </button>

            @if (showUserMenu()) {
              <div
                class="absolute right-0 mt-2 w-56 bg-white rounded-xl border border-gray-300 shadow-lg py-2 overflow-hidden"
              >
                <!-- User Info -->
                <div class="px-4 py-3 border-b border-gray-200">
                  <p class="font-semibold text-gray-900">
                    {{
                      currentUser?.full_name ||
                        currentUser?.first_name ||
                        currentUser?.username ||
                        translationService.t('nav.administrator')
                    }}
                  </p>
                  <p class="text-xs text-gray-500 mt-0.5">
                    {{ currentUser?.email || 'admin@morichal.com' }}
                  </p>
                </div>

                <div class="py-1">
                  <a
                    routerLink="/admin/profile"
                    class="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <i class="fas fa-user text-gray-400 w-4"></i>
                    <span class="text-sm">{{ translationService.t('nav.profile') }}</span>
                  </a>
                  @if (isSuperAdmin) {
                    <a
                      routerLink="/admin/settings"
                      class="flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      <i class="fas fa-cog text-gray-400 w-4"></i>
                      <span class="text-sm">{{ translationService.t('nav.settings') }}</span>
                    </a>
                  }
                </div>

                <div class="border-t border-gray-200 py-1">
                  <button
                    (click)="backToApp()"
                    class="w-full flex items-center gap-3 px-4 py-2.5 text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <i class="fas fa-arrow-left text-gray-400 w-4"></i>
                    <span class="text-sm">{{ translationService.t('adminNav.backToApp') }}</span>
                  </button>
                  <button
                    (click)="logout()"
                    class="w-full flex items-center gap-3 px-4 py-2.5 text-accent-700 hover:bg-accent-50 transition-colors"
                  >
                    <i class="fas fa-sign-out-alt text-accent w-4"></i>
                    <span class="text-sm font-medium">{{
                      translationService.t('nav.signOut')
                    }}</span>
                  </button>
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </header>

    <!-- Page Content -->
    <main class="p-3 lg:p-4">
      <router-outlet></router-outlet>
    </main>
  </div>
</div>
